import { FC, ReactNode, createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { setModal } from 'modules/_shared/redux/modalActions';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';
import { IncentiveOfferOperation } from 'modules/incentivesMarket/types';
import { getIncentivesMarketPurchaseContractTemplateId } from 'modules/incentivesMarket/constants/incentivesMarketSocietyTemplates';
import { generateIncentivesMarketPurchaseContract } from 'modules/incentivesMarket/utils/generateIncentivesMarketPurchaseContract';
import { updateIncentiveOfferOperation } from 'modules/incentivesMarket/redux/incentiveOfferOperationActions';

import { ScreenSizes } from 'constants/sizes';

import { generateIncentiveOfferPurchaseContractModalInitValue } from '../constants/generateIncentiveOfferPurchaseContractModalInitValue';
import { GenerateIncentiveOfferPurchaseContractModalContextType } from '../types/GenerateIncentiveOfferPurchaseContractModalContextType';

const GenerateIncentiveOfferPurchaseContractModalContext =
  createContext<GenerateIncentiveOfferPurchaseContractModalContextType>(
    generateIncentiveOfferPurchaseContractModalInitValue
  );

type ProviderProps = {
  children: ReactNode;
  operation: IncentiveOfferOperation;
  onSuccess?: () => void;
  size?: ScreenSizes;
};

const resolveParticipantId = (
  participant: IncentiveOfferOperation['buyer']
): string => {
  if (typeof participant === 'string') return participant;
  return participant?._id || '';
};

export const GenerateIncentiveOfferPurchaseContractModalProvider: FC<
  ProviderProps
> = ({ children, operation, onSuccess }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [isLoading, setIsLoading] = useState(false);

  const societyId = operation.society;
  const templateId = getIncentivesMarketPurchaseContractTemplateId(actualSociety);

  const handleCloseModal = useCallback(() => {
    dispatch(setModal(null));
  }, [dispatch]);

  const handleSave = useCallback(async () => {
    if (!actualSociety || !templateId) {
      errorSwal(t('ErrorNoIncentivesMarketTemplate'));
      return;
    }

    setIsLoading(true);

    try {
      const contractDoc = await generateIncentivesMarketPurchaseContract({
        dispatch,
        operation,
        societyId,
        society: actualSociety,
        userId: user?._id,
        t,
      });

      await dispatch(
        updateIncentiveOfferOperation(operation._id, {
          purchaseContractDocumentId: contractDoc.documentId,
          buyerId: resolveParticipantId(operation.buyer),
          sellerId: resolveParticipantId(operation.seller),
        } as any)
      );

      successSwal(t('OperationGeneratedContractSuccessfully'));
      handleCloseModal();
      onSuccess?.();
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === 'NO_INCENTIVES_MARKET_TEMPLATE'
      ) {
        errorSwal(t('ErrorNoIncentivesMarketTemplate'));
      } else {
        errorSwal(t('ErrorGeneratingContract'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    actualSociety,
    templateId,
    dispatch,
    operation,
    societyId,
    user?._id,
    t,
    handleCloseModal,
    onSuccess,
  ]);

  const value = useMemo(
    () => ({
      data: {
        isLoading,
        templateId: templateId || '',
        societyId,
        operationId: operation._id,
      },
      actions: {
        handleSave,
        handleCloseModal,
      },
    }),
    [isLoading, templateId, societyId, operation._id, handleSave, handleCloseModal]
  );

  return (
    <GenerateIncentiveOfferPurchaseContractModalContext.Provider value={value}>
      {children}
    </GenerateIncentiveOfferPurchaseContractModalContext.Provider>
  );
};

export const useGenerateIncentiveOfferPurchaseContractModal = () =>
  useContext(GenerateIncentiveOfferPurchaseContractModalContext);
